/**
 * Auth cookie helpers for KGAI-Admin.
 *
 * Ported from KGAI's `frontend/app/utils/token-to-cookie.ts` with minor
 * Vite adaptations (dropped the `typeof window === 'undefined'` SSR guards
 * because this bundle only runs in the browser). Kept behavior otherwise
 * byte-identical so cookies set by www.knowledgegate.ai's login flow are
 * read correctly by admin.knowledgegate.ai.
 *
 * Cookie contract (set by backend on login, visible to all *.knowledgegate.ai):
 *   accessToken    — JWT, 1 hour TTL, domain=.knowledgegate.ai
 *   refreshToken   — opaque refresh token, 30 days TTL, domain=.knowledgegate.ai
 *
 * On localhost we don't set a domain (browsers reject it). On admin, the
 * domain=.knowledgegate.ai means the cookies set by the www login are
 * readable here with zero re-login ceremony.
 */

const KNOWLEDGE_GATE_ROOT_DOMAIN = "knowledgegate.ai";

/**
 * Returns the cookie domain attribute for the current hostname.
 * - On *.knowledgegate.ai hosts: "; domain=.knowledgegate.ai"
 * - On localhost: "" (browsers reject an explicit domain on localhost)
 * - Otherwise: "" (fail safe — don't set a domain we can't vouch for)
 */
function getCookieDomain(): string {
  const hostname = window.location?.hostname;
  if (!hostname) return "";
  if (hostname === "localhost" || hostname === "127.0.0.1") return "";
  if (
    hostname === KNOWLEDGE_GATE_ROOT_DOMAIN ||
    hostname.endsWith(`.${KNOWLEDGE_GATE_ROOT_DOMAIN}`)
  ) {
    return "; domain=.knowledgegate.ai";
  }
  return "";
}

/**
 * Set the access/refresh token cookies. Used after a fresh login/refresh
 * response lands in this SPA. Writes cookies on the .knowledgegate.ai
 * parent domain so they're visible to all subdomains.
 */
export function setAuthCookies(
  accessToken: string,
  refreshToken?: string | null,
): void {
  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:" ? "; secure" : "";

  clearAuthCookies();

  document.cookie = `accessToken=${accessToken}; path=/; max-age=3600; samesite=lax${secure}${domain}`;

  if (refreshToken) {
    document.cookie = `refreshToken=${refreshToken}; path=/; max-age=2592000; samesite=lax${secure}${domain}`;
  }
}

/**
 * Clear both the old host-only cookies and the new .knowledgegate.ai
 * domain cookies. Called on logout and as a best-effort reset when we
 * detect a stale token.
 */
export function clearAuthCookies(): void {
  const domain = getCookieDomain();

  document.cookie = "accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";

  if (domain) {
    document.cookie = `accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
    document.cookie = `refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
  }
}

/**
 * Read a cookie by name. Returns null if absent.
 */
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const lastPart = parts.pop();
    if (lastPart) {
      return lastPart.split(";").shift() ?? null;
    }
  }
  return null;
}

/**
 * Convenience: has the user got an accessToken cookie right now?
 * True does NOT mean the token is valid (could be expired) — only that
 * we have one to try. A 401 from the API means "re-auth needed".
 */
export function hasAuthCookie(): boolean {
  return !!getCookie("accessToken");
}
